// Залы, филиалы/города, номенклатура, условия аренды (выделено из reference.api.ts планом 108, Ф4).
import { api } from '@/lib/api';

import { defined, type ListParams } from './_shared.js';

export interface Hall {
  id: string;
  branchId: string;
  name: string;
  allowParallelPersonal: boolean;
  isArchived: boolean;
  /** Число будущих запланированных занятий зала. */
  upcomingCount: number;
}

// --- Halls (Mobifitness «Залы») — schedule-модуль ---------------------------
// GET /schedule/halls отдаёт snake_case items → мапим в camelCase.
// POST/PATCH возвращают { hall } уже camelCase.

interface HallApi {
  id: string;
  branch_id: string;
  name: string;
  allow_parallel_personal: boolean;
  is_archived: boolean;
  upcoming_count: number;
}

export interface CreateHallBody {
  branchId: string;
  name: string;
  allowParallelPersonal?: boolean;
}

export interface UpdateHallBody {
  name?: string;
  allowParallelPersonal?: boolean;
  isArchived?: boolean;
}

export async function listHalls(params: ListParams = {}): Promise<{ items: Hall[] }> {
  const sp = new URLSearchParams();
  if (params.includeArchived) sp.set('include_archived', 'true');
  const qs = sp.toString();
  const res = await api.get<{ items: HallApi[] }>(`/schedule/halls${qs ? `?${qs}` : ''}`);
  return {
    items: res.items.map((h) => ({
      id: h.id,
      branchId: h.branch_id,
      name: h.name,
      allowParallelPersonal: h.allow_parallel_personal,
      isArchived: h.is_archived,
      upcomingCount: h.upcoming_count ?? 0,
    })),
  };
}

export function createHall(body: CreateHallBody): Promise<{ hall: Hall }> {
  return api.post<{ hall: Hall }>(
    '/schedule/halls',
    defined({
      branch_id: body.branchId,
      name: body.name,
      allow_parallel_personal: body.allowParallelPersonal,
    }),
  );
}

export function updateHall(id: string, body: UpdateHallBody): Promise<{ hall: Hall }> {
  return api.patch<{ hall: Hall }>(
    `/schedule/halls/${id}`,
    defined({
      name: body.name,
      allow_parallel_personal: body.allowParallelPersonal,
      is_archived: body.isArchived,
    }),
  );
}

export function archiveHall(id: string): Promise<void> {
  return api.delete<void>(`/schedule/halls/${id}`);
}

// --- Branches / Cities (Mobifitness «Локация») — branches-модуль ------------
// Ответы camelCase; тела snake_case. Город: существующий cityId ИЛИ новый cityName.

export interface City {
  id: string;
  name: string;
  /** Ответственный по умолчанию для сделок города (план 104 Б7). */
  defaultDealResponsibleEmployeeId: string | null;
}

export interface Branch {
  id: string;
  cityId: string;
  name: string;
  /**
   * Короткое название для тесных мест интерфейса (правка владельца 19.08):
   * плашки, выпадашки, узкие столбцы. Пусто — показываем полное `name`.
   */
  shortName: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  workingHours: string | null;
  timezone: string;
  description: string | null;
  website: string | null;
  latitude: number | null;
  longitude: number | null;
  status: 'active' | 'archived';
  /** Ответственный по умолчанию для новых сделок филиала (план 103). */
  defaultDealResponsibleEmployeeId?: string | null;
  /** Перекрёстные связи: активные залы / группы / сотрудники филиала. */
  hallsCount: number;
  groupsCount: number;
  employeesCount: number;
}

export interface CreateBranchBody {
  name: string;
  shortName?: string | null;
  cityId?: string;
  cityName?: string;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  workingHours?: string | null;
  timezone?: string;
  description?: string | null;
  website?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  /** Ответственный по умолчанию для новых сделок филиала (план 103). */
  defaultDealResponsibleEmployeeId?: string | null;
}

export interface UpdateBranchBody extends CreateBranchBody {
  status?: 'active' | 'archived';
}

function branchPayload(body: CreateBranchBody | UpdateBranchBody): Record<string, unknown> {
  return defined({
    name: body.name,
    short_name: body.shortName,
    city_id: body.cityId,
    city_name: body.cityName,
    address: body.address,
    phone: body.phone,
    email: body.email,
    working_hours: body.workingHours,
    timezone: body.timezone,
    description: body.description,
    website: body.website,
    default_deal_responsible_employee_id: body.defaultDealResponsibleEmployeeId,
    latitude: body.latitude,
    longitude: body.longitude,
    status: (body as UpdateBranchBody).status,
  });
}

export function listBranches(params: ListParams = {}): Promise<{ items: Branch[] }> {
  const sp = new URLSearchParams();
  if (params.includeArchived) sp.set('include_archived', 'true');
  const qs = sp.toString();
  return api.get<{ items: Branch[] }>(`/branches${qs ? `?${qs}` : ''}`);
}

export function listCities(): Promise<{ items: City[] }> {
  return api.get<{ items: City[] }>('/branches/cities');
}

export function createBranch(body: CreateBranchBody): Promise<{ branch: Branch }> {
  return api.post<{ branch: Branch }>('/branches', branchPayload(body));
}

export function updateBranch(id: string, body: UpdateBranchBody): Promise<{ branch: Branch }> {
  return api.patch<{ branch: Branch }>(`/branches/${id}`, branchPayload(body));
}

export function archiveBranch(id: string): Promise<void> {
  return api.delete<void>(`/branches/${id}`);
}

// --- Условия аренды (Mobifitness «Условия аренды») — rental-модуль -----------

export type RentalUnit = 'per_hour' | 'per_booking' | 'per_day';

export interface RentalCondition {
  id: string;
  name: string;
  price: number;
  unit: RentalUnit;
  description: string | null;
  sortOrder: number;
  isArchived: boolean;
  /** Число активных шаблонов абонементов с этим условием. */
  templatesCount: number;
}

export interface CreateRentalConditionBody {
  name: string;
  price?: number;
  unit?: RentalUnit;
  description?: string | null;
  sortOrder?: number;
}
export interface UpdateRentalConditionBody {
  name?: string;
  price?: number;
  unit?: RentalUnit;
  description?: string | null;
  sortOrder?: number;
  isArchived?: boolean;
}

export function listRentalConditions(
  params: ListParams = {},
): Promise<{ items: RentalCondition[] }> {
  const sp = new URLSearchParams();
  if (params.includeArchived) sp.set('include_archived', 'true');
  const qs = sp.toString();
  return api.get<{ items: RentalCondition[] }>(`/rental-conditions${qs ? `?${qs}` : ''}`);
}
export function createRentalCondition(
  body: CreateRentalConditionBody,
): Promise<{ rentalCondition: RentalCondition }> {
  return api.post('/rental-conditions', branchRentalPayload(body));
}
export function updateRentalCondition(
  id: string,
  body: UpdateRentalConditionBody,
): Promise<{ rentalCondition: RentalCondition }> {
  return api.patch(`/rental-conditions/${id}`, branchRentalPayload(body));
}
export function archiveRentalCondition(id: string): Promise<void> {
  return api.delete<void>(`/rental-conditions/${id}`);
}
function branchRentalPayload(
  body: CreateRentalConditionBody | UpdateRentalConditionBody,
): Record<string, unknown> {
  return defined({
    name: body.name,
    price: body.price,
    unit: body.unit,
    description: body.description,
    sort_order: body.sortOrder,
    is_archived: (body as UpdateRentalConditionBody).isArchived,
  });
}
